'use client';

import React, { useState } from 'react';
import { Save, Edit2, User, MapPin, AlertCircle } from 'lucide-react';

interface PersonalData {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  ssn: string;
  nationality: string;
  bio: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  emergencyName: string;
  emergencyRelation: string;
  emergencyPhone: string;
}

const INITIAL: PersonalData = {
  firstName: 'Maria',
  lastName: 'Rodriguez',
  dateOfBirth: '1990-04-15',
  gender: 'Female',
  ssn: '***-**-4821',
  nationality: 'US Citizen',
  bio: 'ICU Registered Nurse with 8+ years of critical care experience. Specialized in cardiac and post-surgical patient care.',
  address: '2847 Maple Grove Drive',
  city: 'Chicago',
  state: 'IL',
  zip: '60614',
  emergencyName: 'Carlos Rodriguez',
  emergencyRelation: 'Spouse',
  emergencyPhone: '(312) 555-0198',
};

export default function PersonalInfo() {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState<PersonalData>(INITIAL);
  const [draft, setDraft] = useState<PersonalData>(INITIAL);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setData(draft);
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const handleCancel = () => {
    setDraft(data);
    setEditing(false);
  };

  const field = (
    label: string,
    key: keyof PersonalData,
    type: string = 'text',
    options?: string[]
  ) => (
    <div className="space-y-1">
      <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">{label}</label>
      {editing ? (
        options ? (
          <select
            value={draft[key]}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          >
            {options.map((o) => <option key={o}>{o}</option>)}
          </select>
        ) : (
          <input
            type={type}
            value={draft[key]}
            onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
            className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        )
      ) : (
        <p className="text-sm text-foreground py-2 px-3 bg-muted/40 rounded-lg">{data[key] || '—'}</p>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      {saved && (
        <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success text-sm px-4 py-2.5 rounded-lg">
          <AlertCircle size={14} />
          Personal information saved successfully.
        </div>
      )}

      {/* Basic Info */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <User size={16} className="text-primary" />
            <h2 className="text-sm font-700 text-foreground">Basic Information</h2>
          </div>
          {!editing ? (
            <button
              onClick={() => { setDraft(data); setEditing(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-500 text-foreground hover:bg-muted transition-colors"
            >
              <Edit2 size={12} />
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-500 text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors"
              >
                <Save size={12} />
                Save Changes
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {field('First Name', 'firstName')}
          {field('Last Name', 'lastName')}
          {field('Date of Birth', 'dateOfBirth', 'date')}
          {field('Gender', 'gender', 'text', ['Female', 'Male', 'Non-binary', 'Prefer not to say'])}
          {field('SSN (masked)', 'ssn')}
          {field('Nationality', 'nationality', 'text', ['US Citizen', 'Permanent Resident', 'Work Visa', 'Other'])}
        </div>
        <div className="mt-4">
          <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Professional Bio</label>
          {editing ? (
            <textarea
              value={draft.bio}
              onChange={(e) => setDraft({ ...draft, bio: e.target.value })}
              rows={3}
              className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
            />
          ) : (
            <p className="mt-1 text-sm text-foreground py-2 px-3 bg-muted/40 rounded-lg leading-relaxed">{data.bio}</p>
          )}
        </div>
      </div>

      {/* Address */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <MapPin size={16} className="text-primary" />
          <h2 className="text-sm font-700 text-foreground">Home Address</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2 lg:col-span-2">{field('Street Address', 'address')}</div>
          {field('City', 'city')}
          {field('State', 'state')}
          {field('ZIP Code', 'zip')}
        </div>
      </div>

      {/* Emergency Contact */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle size={16} className="text-warning" />
          <h2 className="text-sm font-700 text-foreground">Emergency Contact</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {field('Full Name', 'emergencyName')}
          {field('Relationship', 'emergencyRelation', 'text', ['Spouse', 'Parent', 'Sibling', 'Child', 'Friend', 'Other'])}
          {field('Phone Number', 'emergencyPhone', 'tel')}
        </div>
      </div>
    </div>
  );
}
